<template>
  <div class="docs">
    <div class="docs__layout">
      <nav class="docs__toc" aria-label="On this page">
        <div class="docs__toc-title">On this page</div>
        <a v-for="item in toc" :key="item.id" :href="'#' + item.id" @click.prevent="scrollTo(item.id)">{{ item.label }}</a>
        <a :href="referenceUrl" target="_blank" rel="noopener" class="docs__toc-ext">Full API reference <i class="el-icon-top-right"></i></a>
      </nav>

      <div class="docs__body">
        <!-- 快速开始 -->
        <section id="docs-quickstart" class="dev-section">
          <h2 class="dev-section__title">Quickstart</h2>
          <div class="dev-section__sub">Make your first call in under a minute.</div>
          <ol class="docs__steps">
            <li class="dev-card">
              <span class="docs__num">1</span>
              <div>
                <div class="dev-row__title">Create a key</div>
                <div class="dev-row__sub">
                  On the <a href="#" @click.prevent="$emit('go', 'keys')">API keys</a> tab. Copy it when it's shown — it's only shown once.
                </div>
              </div>
            </li>
            <li class="dev-card">
              <span class="docs__num">2</span>
              <div class="docs__grow">
                <div class="dev-row__title">Call the API</div>
                <div class="dev-row__sub">Send the key in the <code>X-API-Key</code> header. This lists your offline machines:</div>
                <code-tabs :samples="samples" class="docs__code"></code-tabs>
              </div>
            </li>
            <li class="dev-card">
              <span class="docs__num">3</span>
              <div class="docs__grow">
                <div class="dev-row__title">Read the response</div>
                <div class="dev-row__sub">Every response has the same shape. Your data is in <code>data</code>; lists come back in pages.</div>
                <copy-field :value="sampleResponse" label="sample response" block class="docs__code"></copy-field>
              </div>
            </li>
          </ol>
        </section>

        <!-- 鉴权 -->
        <section id="docs-auth" class="dev-section">
          <h2 class="dev-section__title">Authentication</h2>
          <div class="dev-card docs__prose">
            <p>Every request needs your key in the <code>X-API-Key</code> header. Calls go to:</p>
            <copy-field :value="baseUrl" label="base URL"></copy-field>
            <p>
              Keys are <b>read-only</b> and only see machines on your own account. API access stays on while at least one of your machines has an active subscription; if they all lapse, calls return
              <code>403</code> until you renew.
            </p>
          </div>
        </section>

        <!-- 接口 -->
        <section id="docs-endpoints" class="dev-section">
          <h2 class="dev-section__title">Endpoints</h2>
          <div class="dev-section__sub">
            Machine numbers are the 15-digit numbers shown on the Machines page. For every field and example, see the
            <a :href="referenceUrl" target="_blank" rel="noopener">full API reference</a>.
          </div>
          <div v-for="group in endpoints" :key="group.group" class="dev-card dev-card--flush docs__group">
            <div class="docs__group-title">{{ group.group }}</div>
            <div v-for="ep in group.items" :key="ep.path" class="docs__ep">
              <div class="docs__ep-line">
                <span class="docs__method">{{ ep.method }}</span>
                <code class="docs__path">{{ ep.path }}</code>
              </div>
              <div class="docs__ep-sum">{{ ep.summary }}</div>
              <div v-if="ep.params" class="docs__ep-params"><span>Parameters:</span> {{ ep.params }}</div>
            </div>
          </div>
        </section>

        <!-- 分页 / 限流 / 错误 -->
        <section id="docs-limits" class="dev-section">
          <h2 class="dev-section__title">Pages, limits and errors</h2>
          <div class="dev-card docs__prose">
            <p>
              <b>Pages.</b> Lists take <code>page</code> (from 1) and <code>size</code> (up to 100) and return <code>records</code> plus the overall <code>total</code>.
            </p>
            <p>
              <b>Rate limit.</b> Your account can make 1,000 calls an hour for each machine with an active subscription, shared by all your keys. For example, 3 paid machines give 3,000 calls an hour. Creating more keys doesn't add more. There's also a burst limit of 20 calls a second. Every response tells you where you stand in <code>X-RateLimit-Limit</code>, <code>X-RateLimit-Remaining</code> and
              <code>X-RateLimit-Reset</code>.
            </p>
            <p><b>Dates.</b> Filters take <code>yyyy-MM-dd HH:mm:ss</code>, for example <code>2026-10-01 00:00:00</code>.</p>
            <table class="docs__errors">
              <thead>
                <tr><th>Status</th><th>What it means</th></tr>
              </thead>
              <tbody>
                <tr v-for="err in errors" :key="err.code">
                  <td><code>{{ err.code }}</code></td>
                  <td>{{ err.meaning }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <!-- AI 助手 -->
        <section id="docs-ai" class="dev-section">
          <h2 class="dev-section__title">Prefer to just ask?</h2>
          <div class="dev-card docs__ai">
            <i class="el-icon-chat-dot-round"></i>
            <div class="docs__grow">
              <div class="dev-row__title">Connect an AI assistant instead</div>
              <div class="dev-row__sub">Claude and ChatGPT can use the same data with no code. Ask “which machines need restocking?” and get an answer.</div>
            </div>
            <el-button size="small" @click="$emit('go', 'mcp')">Set up</el-button>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script>
  // API 文档面板：快速开始 / 鉴权 / 接口 / 分页限流错误。完整字段见 partner-api 的 Scalar 参考页
  import CopyField from "./CopyField";
  import CodeTabs from "./CodeTabs";
  import { API_ENDPOINTS, API_ERRORS, SAMPLE_RESPONSE, codeSamples } from "./developerContent";

  export default {
    name: "DocsPanel",
    components: { CopyField, CodeTabs },
    props: {
      baseUrl: { type: String, required: true },
      referenceUrl: { type: String, required: true }
    },
    data() {
      return {
        endpoints: API_ENDPOINTS,
        errors: API_ERRORS,
        sampleResponse: SAMPLE_RESPONSE,
        toc: [
          { id: "docs-quickstart", label: "Quickstart" },
          { id: "docs-auth", label: "Authentication" },
          { id: "docs-endpoints", label: "Endpoints" },
          { id: "docs-limits", label: "Pages, limits and errors" },
          { id: "docs-ai", label: "AI assistants" }
        ]
      };
    },
    computed: {
      samples() {
        return codeSamples(this.baseUrl);
      }
    },
    methods: {
      scrollTo(id) {
        const el = document.getElementById(id);
        if (!el) {
          return;
        }
        // scrollIntoView 会连带滚动外层 overflow:hidden 的布局容器，把页面顶部顶出可视区；
        // 改为只滚动最近的可滚动容器
        let box = el.parentElement;
        while (box && !(/(auto|scroll)/.test(getComputedStyle(box).overflowY) && box.scrollHeight > box.clientHeight)) {
          box = box.parentElement;
        }
        box = box || document.scrollingElement;
        const top = el.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop - 16;
        box.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
      }
    }
  };
</script>

<style scoped lang="scss">
  .docs__layout {
    display: grid;
    grid-template-columns: 190px minmax(0, 1fr);
    gap: 24px;
    align-items: start;
  }

  .docs__toc {
    position: sticky;
    top: 12px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding-top: 4px;

    a {
      font-size: 13px;
      color: var(--vtm-ink-2);
      text-decoration: none;
      padding: 5px 10px;
      border-radius: var(--vtm-r-sm);

      &:hover {
        background: var(--vtm-well);
        color: var(--vtm-ink);
      }
    }
  }

  .docs__toc-title {
    font-size: 11.5px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--vtm-ink-3);
    padding: 0 10px 6px;
  }

  .docs__toc-ext {
    margin-top: 8px;
    color: var(--vtm-mint-ink) !important;
    font-weight: 600;
  }

  @media (max-width: 900px) {
    .docs__layout {
      grid-template-columns: minmax(0, 1fr);
    }
    .docs__toc {
      display: none;
    }
  }

  .docs__body a {
    color: var(--vtm-mint-ink);
    font-weight: 600;
  }

  code {
    font-family: var(--vtm-mono);
    font-size: 0.9em;
    background: var(--vtm-well);
    border-radius: 4px;
    padding: 1px 5px;
    color: var(--vtm-ink);
  }

  .docs__steps {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;

    > li {
      display: flex;
      gap: 14px;
      align-items: flex-start;
    }
  }

  .docs__num {
    flex-shrink: 0;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: var(--vtm-mint-soft);
    color: var(--vtm-mint-ink);
    font-weight: 700;
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .docs__grow {
    flex: 1;
    min-width: 0;
  }

  .docs__code {
    margin-top: 10px;
  }

  .docs__prose {
    font-size: 13.5px;
    line-height: 1.6;
    color: var(--vtm-ink);

    p {
      margin: 0 0 10px;
    }

    p:last-child {
      margin-bottom: 0;
    }

    .copy-field {
      margin-bottom: 10px;
    }
  }

  .docs__group {
    margin-top: 10px;
  }

  .docs__group-title {
    padding: 10px 16px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--vtm-ink-3);
    background: var(--vtm-surface-2);
    border-bottom: 1px solid var(--vtm-line);
  }

  .docs__ep {
    padding: 12px 16px;
    border-bottom: 1px solid var(--vtm-line);

    &:last-child {
      border-bottom: 0;
    }
  }

  .docs__ep-line {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .docs__method {
    font-family: var(--vtm-mono);
    font-size: 11.5px;
    font-weight: 700;
    color: var(--vtm-ok);
    background: var(--vtm-ok-soft);
    border-radius: 4px;
    padding: 2px 7px;
  }

  .docs__path {
    background: none;
    padding: 0;
    font-size: 13px;
    font-weight: 600;
  }

  .docs__ep-sum {
    margin-top: 4px;
    font-size: 13px;
    color: var(--vtm-ink);
  }

  .docs__ep-params {
    margin-top: 3px;
    font-size: 12.5px;
    color: var(--vtm-ink-2);

    span {
      color: var(--vtm-ink-3);
    }
  }

  .docs__errors {
    width: 100%;
    border-collapse: collapse;
    margin-top: 6px;
    font-size: 13px;

    th,
    td {
      text-align: left;
      padding: 8px 10px;
      border-bottom: 1px solid var(--vtm-line);
      vertical-align: top;
    }

    th {
      font-size: 12px;
      color: var(--vtm-ink-3);
      font-weight: 600;
    }

    td:first-child {
      width: 70px;
    }
  }

  .docs__ai {
    display: flex;
    align-items: center;
    gap: 14px;

    > i {
      font-size: 24px;
      color: var(--vtm-mint-ink);
    }
  }
</style>
